import { Slice } from 'prosemirror-model';
import dataURItoBlob from '../data-uri-to-blob';
import { guidFor } from '@ember/object/internals';
import { updateImageNodeWithProcessingResult, processImageFile } from '../../helpers/image';
import { updateNodeWithUploadResult } from '../../helpers/file';
import { isUrl } from './../../helpers/is-url';
const CUSTOM_EMOJI_PATTERN = /:([a-zA-Z0-9\-]+):/g;
const CUSTOM_EMOJI_CLASS_NAME = 'custom-emoji';
export default function transformPastedSlice(slice, composer, editorView) {
  slice = stripNonBreakingSpaces(slice);
  slice = processPastedHtmlImages(slice, composer, editorView);
  slice = processCustomEmojiText(slice, composer);
  return slice;
}
function stripNonBreakingSpaces(slice) {
  slice.content.nodesBetween(0, slice.content.size, node => {
    if (node.text && node.text.length > 0) {
      node.text = node.text.replace(/\u00A0/g, ' ');
    }
  });
  return slice;
}
function processPastedHtmlImages(slice, composer, editorView) {
  slice.content.forEach(async node => {
    if (composer.config.upload && node.type.name === 'image' && !node.attrs.fileGUID) {
      let allowedTypes = composer.config.upload.allowedImageFileTypes;
      let allowTypesRegex = allowedTypes.includes('*') ? 'image/(?:jpg|jpeg|png|gif)' : `(${allowedTypes.join('|').replace('/', '/')})`;
      let base64Regex = `^data:${allowTypesRegex};base64.*`;
      let validImage = node.attrs.src.match(new RegExp(base64Regex));

      // send the file URL to the upload endpoint if the source is a third party URL
      if (isUrl(node.attrs.src)) {
        let url = node.attrs.src;
        let guid = guidFor(url);
        node.attrs.fileGUID = guid;
        let uploadResult = await composer.commands.doUploadFromUrl(url);
        updateNodeWithUploadResult(editorView, uploadResult, guid);
      }
      if (validImage) {
        let file = dataURItoBlob(node.attrs.src);
        let tempURL = window.URL.createObjectURL(file);
        let guid = guidFor(file);
        file.name = tempURL.split('/').pop();
        node.attrs.src = tempURL;
        node.attrs.fileGUID = guid;
        let processingResult = await processImageFile(file);
        updateImageNodeWithProcessingResult(editorView, processingResult);
        let uploadResult = await composer.commands.doUpload(processingResult.file);
        updateNodeWithUploadResult(editorView, uploadResult);
      }
    }
  });
  return slice;
}
function processCustomEmojiText(slice, composer) {
  if (!composer.config.customEmojiProcessor) {
    return slice;
  }
  let sliceText = slice.content.textBetween(0, slice.content.size);
  if (!sliceText.includes(':')) {
    return slice;
  }
  let processor = composer.config.customEmojiProcessor;
  let schema = slice.content.firstChild?.type.schema;
  if (!schema || !schema.nodes.inlineImage) {
    return slice;
  }
  let transformedNodes = transformSliceNodes(slice.content, schema, processor);
  if (transformedNodes.modified) {
    return new Slice(schema.nodes.doc.create({}, transformedNodes.nodes).content, slice.openStart, slice.openEnd);
  }
  return slice;
}
function transformSliceNodes(content, schema, processor) {
  let modified = false;
  let newNodes = [];
  content.forEach(node => {
    let transformResult = transformNodeRecursively(node, schema, processor);
    newNodes.push(...transformResult.nodes);
    if (transformResult.modified) {
      modified = true;
    }
  });
  return {
    nodes: newNodes,
    modified
  };
}
function transformNodeRecursively(node, schema, processor) {
  if (node.isText && node.text) {
    let textFragments = processTextNodeForEmojis(node, schema, processor);
    let modified = textFragments.length > 1 || textFragments[0] !== node;
    return {
      nodes: textFragments,
      modified
    };
  }
  if (node.content && node.content.size > 0) {
    let childrenModified = false;
    let newChildren = [];
    node.content.forEach(childNode => {
      let transformResult = transformNodeRecursively(childNode, schema, processor);
      newChildren.push(...transformResult.nodes);
      if (transformResult.modified) {
        childrenModified = true;
      }
    });
    if (childrenModified) {
      let newNode = node.copy(node.type.schema.nodes.doc.create({}, newChildren).content);
      return {
        nodes: [newNode],
        modified: true
      };
    }
  }
  return {
    nodes: [node],
    modified: false
  };
}
function processTextNodeForEmojis(textNode, schema, processor) {
  if (!textNode.text || !textNode.text.includes(':')) {
    return [textNode];
  }
  let fragments = [];
  let lastIndex = 0;
  let hasEmojis = false;
  for (let match of textNode.text.matchAll(CUSTOM_EMOJI_PATTERN)) {
    let emojiName = match[1];
    let customEmoji = processor.getCustomEmoji(emojiName);
    if (customEmoji) {
      hasEmojis = true;
      let matchIndex = match.index ?? 0;
      if (matchIndex > lastIndex) {
        let beforeText = textNode.text.substring(lastIndex, matchIndex);
        if (beforeText) {
          fragments.push(schema.text(beforeText, textNode.marks));
        }
      }
      let emojiNode = createEmojiImageNode(schema, customEmoji);
      fragments.push(emojiNode);
      lastIndex = matchIndex + match[0].length;
    }
  }
  if (lastIndex < textNode.text.length) {
    let remainingText = textNode.text.substring(lastIndex);
    if (remainingText) {
      fragments.push(schema.text(remainingText, textNode.marks));
    }
  }
  return hasEmojis ? fragments : [textNode];
}
function createEmojiImageNode(schema, customEmoji) {
  return schema.nodes.inlineImage.create({
    src: customEmoji.src,
    text: customEmoji.alt || customEmoji.identifier,
    className: CUSTOM_EMOJI_CLASS_NAME
  });
}